import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { blogPosts } from '../data/blogPosts';
import SEO from '../components/SEO';
import SchemaOrg from '../components/SchemaOrg';

const BlogPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const categories = Array.from(new Set(blogPosts.map(post => post.category)));

  const filteredPosts = selectedCategory
    ? blogPosts.filter(post => post.category === selectedCategory)
    : blogPosts;

  const blogListSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Blog LQP Global - Tecnología para Negocios",
    "description": "Artículos sobre digitalización, automatización, inteligencia artificial y estrategias de crecimiento para pymes",
    "url": "https://lqpglobal.com/blog",
    "publisher": {
      "@type": "Organization",
      "name": "LQP Global",
      "logo": {
        "@type": "ImageObject",
        "url": "https://lqpglobal.com/logocubo.png"
      }
    },
    "blogPost": blogPosts.map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.metaDescription,
      "url": `https://lqpglobal.com/blog/${post.slug}`,
      "datePublished": post.dateISO,
      "image": post.image
    }))
  };

  return (
    <div className="pt-32 pb-24 bg-[#050505]">
      <SEO
        title="Blog Digital | Tecnología para Negocios Reales"
        description="Artículos sobre digitalización, automatización, inteligencia artificial, e-commerce y estrategias de crecimiento para pymes. Conocimiento práctico para transformar tu negocio."
        keywords="blog pymes, digitalización empresas, automatización negocios, inteligencia artificial empresarial, ecommerce argentina, transformación digital"
        url="https://lqpglobal.com/blog"
      />
      <SchemaOrg type="website" />

      {/* Blog List Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(blogListSchema) }} />

      <div className="container mx-auto px-6">
        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="mb-8">
          <ol className="flex items-center gap-2 text-sm text-gray-500" itemScope itemType="https://schema.org/BreadcrumbList">
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to="/" className="hover:text-white transition-colors" itemProp="item">
                <span itemProp="name">Inicio</span>
              </Link>
              <meta itemProp="position" content="1" />
            </li>
            <span>/</span>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <span className="text-gray-400" itemProp="name">Blog</span>
              <meta itemProp="position" content="2" />
            </li>
          </ol>
        </nav>

        <header className="max-w-4xl mb-16">
          <div className="inline-block px-4 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-xs font-bold text-red-500 mb-6 uppercase tracking-widest">
            Blog Digital & Insights
          </div>
          <h1 className="text-5xl md:text-7xl font-black text-gradient mb-8 leading-tight">
            Tecnología para <br /> <span className="text-red-500">Negocios Reales</span>
          </h1>
          <p className="text-gray-400 text-xl leading-relaxed">
            Compartimos nuestra visión sobre cómo la digitalización y el orden pueden transformar una pyme tradicional en una empresa de elite.
          </p>
        </header>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-3 mb-12">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              selectedCategory === null
                ? 'bg-red-600 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
            }`}
          >
            Todos
          </button>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedCategory === cat
                  ? 'bg-red-600 text-white'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Blog Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredPosts.map((post) => (
            <article
              key={post.id}
              className="group glass rounded-[2.5rem] overflow-hidden border border-white/10 hover:border-white/30 transition-all duration-500 flex flex-col"
              itemScope
              itemType="https://schema.org/BlogPosting"
            >
              <Link to={`/blog/${post.slug}`} className="block">
                <div className="relative overflow-hidden h-64">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                    itemProp="image"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 rounded-full bg-[#050505]/80 backdrop-blur-sm border border-white/20 text-[10px] font-bold text-white uppercase tracking-wider">
                      {post.category}
                    </span>
                  </div>
                </div>
              </Link>

              <div className="p-8 flex flex-col flex-1">
                <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                  <time dateTime={post.dateISO} itemProp="datePublished">{post.date}</time>
                  <span className="w-1 h-1 rounded-full bg-gray-700"></span>
                  <span>{post.readTime} lectura</span>
                </div>

                <h2 className="font-bold text-xl text-white mb-4 group-hover:text-red-500 transition-colors" itemProp="headline">
                  <Link to={`/blog/${post.slug}`}>
                    {post.title}
                  </Link>
                </h2>

                <p className="text-gray-400 text-sm leading-relaxed mb-6 line-clamp-3" itemProp="description">
                  {post.excerpt}
                </p>

                <Link
                  to={`/blog/${post.slug}`}
                  className="mt-auto flex items-center gap-2 text-white font-bold group/btn"
                  itemProp="url"
                >
                  Leer artículo completo
                  <span className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center transition-all group-hover/btn:bg-red-500 group-hover/btn:border-red-500">
                    →
                  </span>
                </Link>
              </div>
            </article>
          ))}
        </div>

        {/* Lead Magnet CTA */}
        <section className="mt-24 glass p-12 md:p-20 rounded-[3rem] border-white/10 text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-red-600/10 blur-[100px] rounded-full pointer-events-none" />
          <div className="relative z-10">
            <h2 className="text-3xl md:text-5xl font-black mb-6">¿Tu negocio es el próximo caso de éxito?</h2>
            <p className="text-gray-400 mb-10 max-w-2xl mx-auto text-lg">
              Dejá de adivinar y empezá a medir. Construyamos juntos el sistema que te va a permitir escalar sin límites.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/contacto"
                className="px-8 py-4 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-all shadow-xl shadow-red-600/20"
              >
                Agendar Consultoría Gratuita
              </Link>
              <Link
                to="/soluciones"
                className="px-8 py-4 glass text-white font-bold rounded-xl border-white/10 hover:bg-white/5 transition-all"
              >
                Ver Soluciones
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default BlogPage;
